import React, { useEffect, useMemo, useState } from 'react';
import {
  useGetElearningJsonQuery,
  useGetManifestQuery,
  useGetSignedCookiesAdminQuery,
  useGetQuizJsonQuery,
  useGetHelpJsonQuery,
  useGetElearningBehaviourSettingsQuery,
  useGetVideoPreviewLinkQuery,
} from '@/modules/services';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import PreviewReviewRoutes, {
  PreviewReviewParamType,
} from '@/pages/PreviewReview/routes';
import PlayerUi from '@/components/Player';

import { useThemeStyle } from '@/modules/hooks/useThemeStyle';

import {
  getCurrentFrame,
  getPublishedEntityLocation,
  hasVocamData,
  processQuestion,
  replaceContentAssetLink,
  useIsMobileScreenSize,
} from '@/lib/util';
import { AnswerDataType, QuestionDataType } from '@/modules/types/question';
import { appData } from '@/lib/data';
import { FaqType, HelperType } from '@/modules/types/helper';
import { PreviewLivePath } from '@/pages/PreviewLive/consts';
import { useModuleStyle } from '@/modules/hooks/useModuleStyle';

const PreviewReview = (): React.ReactElement => {
  const history = useHistory();
  const { tenantId, elearningId, type, frameId } =
    useParams<PreviewReviewParamType>();
  const [currentFrameId, setCurrentFrameId] = useState(frameId);
  const [isQuizReady, setIsQuizReady] = useState(false);

  const { data: elearningBehaviourSettingsData } =
    useGetElearningBehaviourSettingsQuery();

  const { data: manifestData } = useGetManifestQuery({
    type,
    tenantId,
    elearningId,
  });

  const { isSuccess } = useGetSignedCookiesAdminQuery(
    {
      tenantId,
      type,
      elearningId,
      ...(manifestData?.Item?.elearningIdentifier && {
        elearningIdentifier: `${manifestData?.Item?.elearningIdentifier}`,
      }),
      ...(manifestData?.Item?.quizIdentifier && {
        quizIdentifier: `${manifestData?.Item?.quizIdentifier}`,
      }),
      ...(manifestData?.Item?.elearningIdentifier && {
        themeId: `${manifestData?.Item?.themeUUID}`,
      }),
    },
    { skip: !manifestData || !manifestData.Item },
  );

  const { data: elearningData } = useGetElearningJsonQuery(
    {
      tenantId,
      type,
      entityId: elearningId,
      identifier: manifestData?.Item?.elearningIdentifier,
      entity: 'elearnings',
      contentType: 'published-content',
    },
    { skip: !isSuccess || !manifestData?.Item },
  );

  const currentFrame = useMemo(
    () => getCurrentFrame(elearningData?.frames ?? [], currentFrameId ?? ''),
    [elearningData?.frames, currentFrameId],
  );
  // we don't need to get the video link by frameId, cause we already have the frame data from elearningJson, we change it to use useGetVideoPreviewLinkQuery to get the video link by moduleId and topicId
  const { data: vocamVideoLink } = useGetVideoPreviewLinkQuery(
    {
      moduleId: currentFrame?.vocamMetadata.moduleId as number,
      topicId: currentFrame?.vocamMetadata.topicId as number,
    },
    {
      skip:
        !currentFrame?.vocamMetadata.moduleId ||
        !currentFrame?.vocamMetadata.topicId ||
        !hasVocamData(currentFrame),
    },
  );

  const { data: quizData } = useGetQuizJsonQuery(
    {
      tenantId,
      type,
      entityId: elearningId,
      identifier: manifestData?.Item?.elearningIdentifier,
      entity: 'quizzes',
      contentType: 'published-content',
    },
    { skip: !isSuccess || !manifestData?.Item },
  );

  // passing undefined queryArgs as we don't need them but need to pass the queryOptions to skip requests until we have signed cookies
  const { data: helpData } = useGetHelpJsonQuery(undefined, {
    skip: !isSuccess,
  });

  useThemeStyle(elearningData?.theme);
  useModuleStyle(elearningData);

  const { isMobile } = useIsMobileScreenSize();

  useEffect(() => {
    if (!elearningData?.frames || elearningData.frames.length === 0) return;

    const frameIdNotFound =
      currentFrameId &&
      !elearningData.frames.some((f) => f.id === currentFrameId);

    if (!currentFrameId || frameIdNotFound) {
      const firstFrameId = elearningData.frames[0].id;
      setCurrentFrameId(firstFrameId);
      const routePath =
        type === 'live' ? PreviewLivePath : PreviewReviewRoutes.path;
      history.replace(
        generatePath(routePath, {
          tenantId,
          elearningId,
          type,
          frameId: firstFrameId,
        }),
      );
    }
  }, [elearningData, currentFrameId]);

  if (
    !manifestData ||
    !manifestData.Item ||
    !isSuccess ||
    !elearningData ||
    !elearningData.frames ||
    !quizData ||
    !elearningBehaviourSettingsData
  ) {
    return <div className="loading" />;
  }

  const playerHandlerState = {
    questionsToAsk: quizData.quizMetadata.questionNo,
    questionsAsked: 0,
    correctAnswers: 0,
    modifiedQuestions: quizData.questions as QuestionDataType[],
    questionsCompleted: [],
  };

  const playerHandlers = {
    submitResult: (result: any, callback: () => void) => {
      // @todo - submit result to TMS
    },
    submitQuestion: (
      answer: AnswerDataType,
      callback: (payload: any) => void,
    ) => {
      const nextPayload = processQuestion(answer, playerHandlerState);
      callback(nextPayload);
    },
    getQuestions: () => {
      // @todo
      playerHandlerState.questionsAsked = 0;
      playerHandlerState.correctAnswers = 0;
      playerHandlerState.modifiedQuestions =
        quizData.questions as QuestionDataType[];
      playerHandlerState.questionsCompleted = [];
    },
    submitAssessment: (
      assessmentData: any,
      callback: (payload: { score: number }) => void,
    ) => {
      const score = Math.round(
        (playerHandlerState.correctAnswers / quizData.quizMetadata.questionNo) *
          100,
      );

      callback({ score });
    },
    startAssessment: () => {
      setIsQuizReady(true);
    },
    moveToFrame: (nextFrameId: string) => {
      setCurrentFrameId(nextFrameId);
      // default set it to review path
      let routePath = PreviewReviewRoutes.path;
      switch (type) {
        case 'live':
          // Importing path directly to remove circular dependencies
          routePath = PreviewLivePath;
          break;
        default:
          break;
      }
      const nextPath = generatePath(routePath, {
        tenantId,
        elearningId,
        type,
        frameId: nextFrameId,
      });
      history.push(nextPath);
    },
  };

  const publishedAssetLink = getPublishedEntityLocation({
    contentType: 'published-content-assets',
    tenantId,
    type,
    entity: 'elearnings',
    entityId: elearningId,
    identifier: manifestData.Item.elearningIdentifier,
  });

  const frames = elearningData.frames.map((frame) => {
    const content = replaceContentAssetLink({
      content: frame.content,
      newLink: publishedAssetLink,
      tenantId,
      elearningId,
    });
    return { ...frame, content };
  });

  const elearning = { ...elearningData };

  if (!elearning.theme) {
    elearning.theme = appData.playerTheme.default;
  }

  return (
    <PlayerUi
      isPlayable={true}
      currentFrameId={currentFrameId}
      isQuizEnabled={isQuizReady}
      elearning={elearning}
      frames={frames}
      quiz={quizData}
      resources={elearning.resources || []}
      helperContent={
        helpData
          ? helpData.content
          : (appData.helpJson.default.content as HelperType[])
      }
      helperFaq={
        helpData ? helpData.faqs : (appData.helpJson.default.faqs as FaqType[])
      }
      handlers={playerHandlers}
      isMobileView={isMobile}
      vocamVideoLink={vocamVideoLink}
      elearningBehaviourSettings={elearningBehaviourSettingsData}
    />
  );
};

export default PreviewReview;
